import React, { useMemo, useState } from 'react';
import config from '../config';
import {
  WEEKDAYS_SK_SHORT,
  addDays,
  diffDays,
  fromKey,
  monthLabelSk,
  toKey,
} from '../utils/dates';
import { seasonForDate } from '../utils/pricing';
import Icon from './Icons';

/**
 * Kalendár dostupnosti s výberom termínu v štýle Airbnb.
 * Zobrazuje jeden veľký mesiac (vzhľad Google Kalendára) – pod číslom dňa
 * je základná sezónna cena. Obsadené dni sú prečiarknuté v červenom tóne.
 * - obsadené a minulé dni sa nedajú vybrať
 * - vybrať možno len súvislý voľný úsek
 * - dĺžka sa počíta v dňoch vrátane prvého aj posledného
 *   (min. config.minDays, max. config.maxDays)
 */
export default function BookingCalendar({ occupiedSet, startKey, endKey, onChange, status }) {
  const [monthOffset, setMonthOffset] = useState(0);
  const [hoverKey, setHoverKey] = useState(null);
  const [hint, setHint] = useState(null);

  const today = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);

  const todayStr = toKey(today);
  const monthDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);

  /** Základná sezónna cena pre daný deň (bez zľavy za dlhý pobyt). */
  const basePrice = (dayKey) => config.seasons[seasonForDate(dayKey)].price;

  /** Je celý úsek start..end voľný? (koncový deň kontroluje klik na tlačidlo) */
  const rangeIsFree = (aKey, bKey) => {
    let cursor = fromKey(aKey);
    const end = fromKey(bKey);
    while (cursor < end) {
      if (occupiedSet.has(toKey(cursor))) return false;
      cursor = addDays(cursor, 1);
    }
    return true;
  };

  const handleDayClick = (dayKey) => {
    setHint(null);
    if (dayKey < todayStr || occupiedSet.has(dayKey)) return;

    // Nový výber (žiadny začiatok, alebo už je vybraný celý termín)
    if (!startKey || (startKey && endKey)) {
      onChange(dayKey, null);
      return;
    }

    // Klik pred/na začiatok = nový začiatok
    if (dayKey <= startKey) {
      onChange(dayKey, null);
      return;
    }

    const days = diffDays(startKey, dayKey) + 1; // prvý aj posledný deň sa platia
    if (days < config.minDays) {
      setHint(`Minimálny prenájom sú ${config.minDays} dni.`);
      return;
    }
    if (days > config.maxDays) {
      setHint(`Maximálny prenájom je ${config.maxDays} dní.`);
      return;
    }
    if (!rangeIsFree(startKey, dayKey)) {
      setHint('Vybraný úsek obsahuje obsadené dni. Vyber kratší termín.');
      return;
    }
    onChange(startKey, dayKey);
  };

  /** Bunky aktuálneho mesiaca vrátane prázdnych na za/dokončenie týždňov. */
  const renderCells = () => {
    const firstWeekday = (monthDate.getDay() + 6) % 7; // pondelok = 0
    const daysInMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate();
    const cells = [];

    for (let i = 0; i < firstWeekday; i += 1) {
      cells.push(<span key={`pre-${i}`} className="cal__day cal__day--empty" aria-hidden="true" />);
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
      const dayKey = toKey(date);
      const isPast = dayKey < todayStr;
      const isOccupied = occupiedSet.has(dayKey);
      const disabled = isPast || isOccupied;

      const isStart = dayKey === startKey;
      const isEnd = dayKey === endKey;
      const inRange = startKey && endKey && dayKey > startKey && dayKey < endKey;
      const inHoverRange =
        startKey && !endKey && hoverKey && dayKey > startKey && dayKey <= hoverKey;

      const classes = [
        'cal__day',
        isPast && 'cal__day--past',
        isOccupied && !isPast && 'cal__day--occupied',
        (isStart || isEnd) && 'cal__day--edge',
        inRange && 'cal__day--range',
        inHoverRange && !disabled && 'cal__day--hover-range',
        dayKey === todayStr && 'cal__day--today',
      ]
        .filter(Boolean)
        .join(' ');

      const price = basePrice(dayKey);
      const ariaLabel = `${day}. ${monthLabelSk(monthDate)}${
        isOccupied ? ' – obsadené' : isPast ? '' : ` – ${price} € za deň`
      }`;

      cells.push(
        <button
          key={dayKey}
          type="button"
          className={classes}
          disabled={disabled}
          onClick={() => handleDayClick(dayKey)}
          onMouseEnter={() => setHoverKey(dayKey)}
          onFocus={() => setHoverKey(dayKey)}
          aria-pressed={isStart || isEnd}
          aria-label={ariaLabel}
        >
          <span className="cal__num">{day}</span>
          {!isPast && (
            <span className="cal__price">{isOccupied ? 'obsadené' : `${price} €`}</span>
          )}
        </button>
      );
    }

    // Doplnenie posledného týždňa, aby mriežka mala rovné okraje.
    while (cells.length % 7 !== 0) {
      cells.push(
        <span key={`post-${cells.length}`} className="cal__day cal__day--empty" aria-hidden="true" />
      );
    }
    return cells;
  };

  return (
    <div className="cal">
      <div className="cal__header">
        <p className="cal__title">{monthLabelSk(monthDate)}</p>
        <div className="cal__nav-group">
          <button
            type="button"
            className="cal__nav"
            onClick={() => setMonthOffset((value) => Math.max(0, value - 1))}
            disabled={monthOffset === 0}
            aria-label="Predchádzajúci mesiac"
          >
            <Icon name="arrow" style={{ transform: 'rotate(180deg)' }} size={18} />
          </button>
          <button
            type="button"
            className="cal__nav"
            onClick={() =>
              setMonthOffset((value) => Math.min(config.calendarMonthsAhead - 1, value + 1))
            }
            disabled={monthOffset >= config.calendarMonthsAhead - 1}
            aria-label="Ďalší mesiac"
          >
            <Icon name="arrow" size={18} />
          </button>
        </div>
      </div>

      <p className="cal__legend">
        <span className="cal__legend-item"><i className="cal__dot cal__dot--free" /> voľné</span>
        <span className="cal__legend-item"><i className="cal__dot cal__dot--occupied" /> obsadené</span>
        <span className="cal__legend-item"><i className="cal__dot cal__dot--selected" /> tvoj výber</span>
      </p>

      {status === 'loading' && (
        <p className="cal__status" role="status">Načítavam dostupnosť…</p>
      )}
      {status === 'error' && (
        <p className="cal__status cal__status--error" role="alert">
          Kalendár dostupnosti sa nepodarilo načítať. Skús to prosím neskôr,
          alebo nás kontaktuj priamo.
        </p>
      )}

      <div className="cal__weekdays" aria-hidden="true">
        {WEEKDAYS_SK_SHORT.map((weekday) => (
          <span key={weekday}>{weekday}</span>
        ))}
      </div>
      <div className="cal__grid" onMouseLeave={() => setHoverKey(null)}>
        {renderCells()}
      </div>

      {hint && (
        <p className="cal__hint" role="alert">{hint}</p>
      )}
    </div>
  );
}
